import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import {
  rolePermissionService,
  PmRoleCreate,
  PmRoleUpdate,
  PmRoleClaimCreate,
  PmRoleClaimUpdate,
  PmUserRoleAssign,
} from '../../../services/modules/userManagement/rolePermission.service';
import {
  RsRole,
  RsPermission,
  RsRoleClaim,
  RsUserRoleItem,
} from '../../../models/user-management/RolePermissionModels';
import { RsValue } from '../../../models/base/BaseModel';

export class RolePermissionStore {
  rootStore: RootStore;
  roles: RsRole[] = [];
  permissions: RsPermission[] = [];
  roleClaims: RsRoleClaim[] = [];
  userRoles: RsUserRoleItem[] = [];
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  fetchRoles = async () => {
    this.loading = true;
    this.error = null;
    try {
      const data = await rolePermissionService.getRoles();
      runInAction(() => {
        this.roles = Array.isArray(data) ? data : [];
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : 'Roller yüklenemedi';
      });
      throw e;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  fetchPermissions = async () => {
    this.loading = true;
    this.error = null;
    try {
      const data = await rolePermissionService.getPermissions();
      runInAction(() => {
        this.permissions = Array.isArray(data) ? data : [];
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : 'Yetkiler yüklenemedi';
      });
      throw e;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  fetchRoleClaims = async () => {
    this.loading = true;
    this.error = null;
    try {
      const data = await rolePermissionService.getRoleClaims();
      runInAction(() => {
        this.roleClaims = Array.isArray(data) ? data : [];
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : 'Rol yetkileri yüklenemedi';
      });
      throw e;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  fetchUserRoles = async () => {
    this.loading = true;
    this.error = null;
    try {
      const data = await rolePermissionService.getUserRoles();
      runInAction(() => {
        this.userRoles = Array.isArray(data) ? data : [];
      });
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : 'Kullanıcı-rol atamaları yüklenemedi';
      });
      throw e;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  refreshAll = async () => {
    await Promise.all([
      this.fetchRoles(),
      this.fetchPermissions(),
      this.fetchRoleClaims(),
      this.fetchUserRoles(),
    ]);
  };

  createRole = async (data: PmRoleCreate): Promise<RsValue<RsRole>> => {
    const res = await rolePermissionService.createRole(data);
    if (!res?.error) await this.fetchRoles();
    return res;
  };

  updateRole = async (id: string, data: PmRoleUpdate): Promise<RsValue<boolean>> => {
    const res = await rolePermissionService.updateRole(id, data);
    if (!res?.error) await this.fetchRoles();
    return res as RsValue<boolean>;
  };

  deleteRole = async (id: string): Promise<RsValue<boolean>> => {
    const res = await rolePermissionService.deleteRole(id);
    if (!res?.error) await this.fetchRoles();
    return res as RsValue<boolean>;
  };

  createRoleClaim = async (data: PmRoleClaimCreate): Promise<RsValue<number>> => {
    const res = await rolePermissionService.createRoleClaim(data);
    if (!res?.error) await this.fetchRoleClaims();
    return res;
  };

  updateRoleClaim = async (id: number, data: PmRoleClaimUpdate): Promise<RsValue<boolean>> => {
    const res = await rolePermissionService.updateRoleClaim(id, data);
    if (!res?.error) await this.fetchRoleClaims();
    return res;
  };

  deleteRoleClaim = async (id: number): Promise<RsValue<boolean>> => {
    const res = await rolePermissionService.deleteRoleClaim(id);
    if (!res?.error) await this.fetchRoleClaims();
    return res;
  };

  assignUserRole = async (data: PmUserRoleAssign): Promise<RsValue<boolean>> => {
    const res = await rolePermissionService.assignUserRole(data);
    if (!res?.error) await this.fetchUserRoles();
    return res;
  };

  removeUserRole = async (data: PmUserRoleAssign): Promise<RsValue<boolean>> => {
    const res = await rolePermissionService.removeUserRole(data);
    if (!res?.error) await this.fetchUserRoles();
    return res;
  };
}
